import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Chip } from '../ModuleFrame';
import { apiGet, apiPost } from '../../utils/backendApi';
import { friendlyError } from '../../data/entities';
import { LAB_PHASES, LAB_SECTIONS } from '../../data/consoleCatalog';

// Shared bits for the Lab workbench phases ([[lab-system]]).

// The phases and their rail sections are catalog data (consoleCatalog
// LAB_PHASES / LAB_SECTIONS) so NoVA, the guide and this rail agree.
export const PHASES = LAB_PHASES;
export { LAB_SECTIONS };
export const FREE_PHASES = new Set(LAB_PHASES.filter((p) => p.free).map((p) => p.id));

export const ACTIVE = new Set(['queued', 'planning', 'running']);
export const tone = (s) => (s === 'done' ? 'ok' : s === 'failed' ? 'danger' : s === 'awaiting_approval' ? 'warn' : ACTIVE.has(s) ? 'info' : 'muted');
export const when = (ms) => (ms ? new Date(ms).toLocaleString() : '—');
export const ago = (ms) => {
  if (!ms) return '';
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};
export const statusLabel = (s) => (s === 'awaiting_approval' ? 'needs approval' : s);

export const KIND_LABEL = { app: 'app design', agent: 'agent instructions', knowledge: 'knowledge draft', testCase: 'test scenario', idea: 'idea', story: 'user story', concept: 'concept' };

// Screens → the live voice session (CopilotPanel listens): a system note
// NoVA reads before her next turn. `runId` names the run "stop" would cancel.
export function tellNova(text, { respond = true, runId } = {}) {
  window.dispatchEvent(new CustomEvent('nv:voice-context', { detail: { text, respond, runId } }));
}

// ?phase=&id=&node=&fig=&screen=&present=&revise= in the URL so Operate, composers, the
// strip and the voice agent's navigate_app can deep-link (the concept board
// is driven figure by figure through &fig=).
// `defaultPhase` is the phase nextStep names when the URL carries none, so
// opening the Lab lands on the thing to do. `card` scrolls Design to one
// of its cards (knowledge | data | policies — the old tab names).
export function useLabParams(defaultPhase = 'design') {
  const [params, setParams] = useSearchParams();
  const phase = params.get('phase') || defaultPhase;
  const id = params.get('id') || null;
  const node = params.get('node') || null;
  const fig = params.get('fig') || null;
  const screen = params.get('screen') || null;
  const card = params.get('card') || null;
  const present = params.get('present') === '1';
  const revise = params.get('revise') === '1';
  const set = (patch) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) { if (v == null || v === '' || v === false) next.delete(k); else next.set(k, v === true ? '1' : String(v)); }
    setParams(next, { replace: true });
  };
  // `screen` focuses one concept screen on the Screens figure (ScreenReview);
  // changing figure or phase clears it. `revise=1` opens the concept's
  // revision panel (notes + feedback) — survives figure changes, so notes
  // can be collected figure by figure.
  return { phase, id, node, fig, screen, card, present, revise, setPhase: (p) => set({ phase: p, id: null, node: null, fig: null, screen: null, card: null, present: null, revise: null }), setId: (v) => set({ id: v }), setNode: (v) => set({ node: v }), setFig: (v) => set({ fig: v, screen: null }), setScreen: (v) => set({ screen: v, ...(v ? { fig: 'screens' } : {}) }), setPresent: (v) => set({ present: v }), setRevise: (v) => set({ revise: v }), go: (p, v = null, extra = {}) => set({ phase: p, id: v, node: null, fig: null, screen: null, card: null, present: null, revise: null, ...extra }) };
}

// One busy key + one error line per phase screen.
export function useAct() {
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const act = async (key, fn) => {
    setBusy(key); setErr('');
    try { return await fn(); } catch (ex) { setErr(friendlyError(ex)); return null; } finally { setBusy(''); }
  };
  return { busy, err, act, setErr };
}

export function patchText(p) {
  const x = p.patch || {};
  if (p.kind === 'app') return [x.goal ? `Purpose:\n${x.goal}` : null, x.users ? `Users:\n${x.users}` : null].filter(Boolean).join('\n\n');
  if (p.kind === 'agent') return x.systemPrompt || '';
  if (p.kind === 'knowledge') return `# ${x.title || ''}\n\n${x.content || ''}`;
  if (p.kind === 'testCase') return `${x.name || ''} (${x.env || 'uat'})\n\n${x.scenario || ''}`;
  if (p.kind === 'idea') return [x.body, x.value ? `Value: ${x.value}` : null].filter(Boolean).join('\n\n');
  if (p.kind === 'story') return `As a ${x.asA || '…'}, I want ${x.iWant || '…'} so that ${x.soThat || '…'}.\n\n${(x.acceptance || []).map((c, i) => `${i + 1}. ${c}`).join('\n')}`;
  return JSON.stringify(x, null, 2);
}

// before → after for app / agent patches; plain text otherwise.
export function PatchPreview({ p, app, agents }) {
  const x = p.patch || {};
  if (p.kind === 'app') {
    return (
      <div className="lab-diff">
        {x.goal ? <div><span className="lab-diff-k">Purpose</span><div className="lab-diff-before">{app?.goal || <em>empty</em>}</div><div className="lab-diff-after">{x.goal}</div></div> : null}
        {x.users ? <div><span className="lab-diff-k">Users</span><div className="lab-diff-before">{app?.users || <em>empty</em>}</div><div className="lab-diff-after">{x.users}</div></div> : null}
      </div>
    );
  }
  if (p.kind === 'agent') {
    const a = (agents || []).find((g) => g.id === x.agentId);
    return (
      <div className="lab-diff">
        <div><span className="lab-diff-k">{a?.name || x.agentId} · instructions</span><div className="lab-diff-before">{a?.systemPrompt || <em>empty</em>}</div><div className="lab-diff-after">{x.systemPrompt}</div></div>
      </div>
    );
  }
  return <pre className="lab-md">{patchText(p)}</pre>;
}

export function ProposalCard({ org, run, p, canApply, busy, act, app, agents, onApplied }) {
  const key = `apply-${run.id}-${p.id}`;
  const label = p.kind === 'idea' ? 'Add to ideas' : p.kind === 'story' ? 'Add to stories' : 'Apply';
  return (
    <div className={`lab-proposal ${p.applied ? 'is-applied' : ''}`}>
      <div className="lab-proposal-head">
        <Chip tone={p.kind === 'idea' ? 'info' : p.kind === 'story' ? 'info' : 'muted'}>{KIND_LABEL[p.kind] || p.kind}</Chip>
        <strong>{p.title}</strong>
        <span style={{ flex: 1 }} />
        {p.applied ? <Chip tone="ok">{p.kind === 'idea' || p.kind === 'story' ? 'added' : 'applied'} {ago(p.applied.at)}</Chip> : canApply ? (
          <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={busy === key} onClick={() => act(key, async () => { const out = await apiPost(`/lab/${org.id}/runs/${run.id}/apply`, { proposalId: p.id }); onApplied?.(p, out); })}>{busy === key ? 'Working…' : label}</button>
        ) : null}
      </div>
      {p.rationale && <p className="mf-muted" style={{ margin: '4px 0 0' }}>{p.rationale}</p>}
      <details className="lab-patch"><summary className="mf-muted">What changes</summary><PatchPreview p={p} app={app} agents={agents} /></details>
    </div>
  );
}

// Screenshot: presigned on demand (keys never reach the browser as URLs).
export function Shot({ org, runId, k, large = false, auto = false, alt = 'Screenshot' }) {
  const [url, setUrl] = useState('');
  const [err, setErr] = useState('');
  const show = async () => {
    try { const out = await apiGet(`/lab/${org.id}/runs/${runId}/artifacts?key=${encodeURIComponent(k)}`); setUrl(out.url); } catch (ex) { setErr(friendlyError(ex)); }
  };
  React.useEffect(() => { if (auto && k && !url) show(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [k, auto]);
  if (err) return <span className="cs-form-error lab-step-sub">{err}</span>;
  if (!url) return auto ? <div className="lab-shot-wait mf-muted">Loading screenshot…</div> : <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={show}>Screenshot</button>;
  return <a href={url} target="_blank" rel="noreferrer" className={large ? 'lab-shot is-large' : 'lab-shot'} title="Open full size"><img src={url} alt={alt} loading="lazy" /></a>;
}

// Composer at the top of a list pane: brief + optional target + submit.
export function Composer({ placeholder, buttonLabel, busyLabel = 'Starting…', canEdit, busy, onStart, rows = 3, targets = null, target, setTarget, approveWritesOn = [], note = null, minLen = 8, allowEmpty = false, children }) {
  const [brief, setBrief] = useState('');
  const ok = allowEmpty || brief.trim().length >= minLen;
  return (
    <div className="lab-compose">
      <textarea className="cs-input lab-brief" rows={rows} value={brief} onChange={(e) => setBrief(e.target.value)} placeholder={placeholder} disabled={!canEdit} aria-label="Brief" />
      <div className="lab-compose-row">
        {targets && (targets.length ? (
          <label className="mf-muted lab-target">Target
            <select className="cs-input" value={target} onChange={(e) => setTarget(e.target.value)} aria-label="Surface">
              {targets.map((t) => <option key={t.env} value={t.env} disabled={t.status && t.status !== 'live'}>{t.label}{t.status && t.status !== 'live' ? ` — ${t.status}` : ''}</option>)}
            </select>
          </label>
        ) : <span className="cs-help">No hosted surface yet — link an instance under Surfaces.</span>)}
        {targets && approveWritesOn.includes(target) && <span className="cs-help">Steps that change data on <b>{target}</b> wait for an admin.</span>}
        <span style={{ flex: 1 }} />
        <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" disabled={!canEdit || busy || !ok || (targets && !targets.length)} onClick={async () => { const done = await onStart(brief.trim()); if (done !== false) setBrief(''); }}>{busy ? busyLabel : buttonLabel}</button>
      </div>
      {note}
      {children}
    </div>
  );
}

// Feedback on ONE story or idea → POST …/artifacts/:id/revise → a refine run
// rewrites it as a new `proposed` artifact and supersedes this one. Shared
// by the Stories and Ideas boards (the concept board has its own round of
// notes in Concept.js).
export function ReviseBox({ org, artifact, what = 'story', busy, act, onClose, onStarted }) {
  const [text, setText] = useState('');
  const key = `revise-${artifact.id}`;
  const send = () => act(key, async () => {
    const out = await apiPost(`/lab/${org.id}/artifacts/${artifact.id}/revise`, { feedback: text.trim() });
    setText('');
    onStarted?.(out);
  });
  return (
    <div className="lab-compose" role="group" aria-label={`Revise this ${what}`}>
      <textarea className="cs-input lab-brief" rows={3} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && text.trim().length >= 8) send(); }} placeholder={what === 'story' ? 'What should change? e.g. “Make it the manager, not the agent, and add a criterion that the export is a CSV.”' : 'What should change? e.g. “Narrow it to the weekly digest; drop the marketplace angle.”'} aria-label="What should change" autoFocus />
      <div className="lab-compose-row">
        <span className="cs-help">A rewritten {what} lands on the board as proposed; this one is marked superseded. Counts as a refine run.</span>
        <span style={{ flex: 1 }} />
        <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={onClose}>Cancel</button>
        <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" disabled={text.trim().length < 8 || busy === key} onClick={send}>{busy === key ? 'Starting…' : 'Revise'}</button>
      </div>
    </div>
  );
}

export function EmptyDetail({ phase, children }) {
  const p = PHASES.find((x) => x.id === phase);
  return (
    <div className="lab-empty">
      <h3>{p?.label}</h3>
      <p className="mf-muted">{p?.blurb}</p>
      {children}
    </div>
  );
}

export function RunCard({ run, selected, onClick, extra = null }) {
  const r = run;
  return (
    <button type="button" className={`lab-card ${selected ? 'is-on' : ''}`} onClick={onClick} aria-pressed={selected}>
      <div className="lab-card-row">
        <Chip tone={tone(r.status)}>{statusLabel(r.status)}</Chip>
        {r.target?.env && <Chip>{r.target.env}</Chip>}
        {r.findings?.verdict && <Chip tone={r.findings.verdict === 'pass' ? 'ok' : 'danger'}>{r.findings.verdict}</Chip>}
        <span style={{ flex: 1 }} />
        <span className="mf-muted lab-card-meta">{ACTIVE.has(r.status) ? (r.stage || '') : ago(r.finishedAt || r.updatedAt || r.createdAt)}</span>
      </div>
      <div className="lab-card-brief">{r.brief}</div>
      {extra}
    </button>
  );
}

export const fmtSec = (s) => {
  const n = Math.max(0, Math.round(s));
  if (n < 60) return `${n}s`;
  const m = Math.floor(n / 60); const r = n % 60;
  return r && m < 10 ? `${m}m ${r}s` : `${m} min`;
};

// Elapsed since the run started working (startedAt; createdAt while queued)
// and the remaining time against the organization's typical duration for
// this kind (info.eta from GET /lab/:org/apps/:app) — ticks every second.
export function useRunClock(run, etaSec) {
  const [, tick] = useState(0);
  const active = run && ACTIVE.has(run.status);
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [active]);
  if (!run) return { elapsed: 0, remaining: null, over: false };
  const from = run.startedAt || run.createdAt || Date.now();
  const elapsed = Math.max(0, (Date.now() - from) / 1000);
  if (!etaSec) return { elapsed, remaining: null, over: false };
  const remaining = etaSec - elapsed;
  return { elapsed, remaining: Math.max(0, remaining), over: remaining < -15 };
}

export function RunClock({ run, etaSec }) {
  const { elapsed, remaining, over } = useRunClock(run, etaSec);
  if (!run || !ACTIVE.has(run.status)) return null;
  return (
    <span className="lab-clock mf-muted" title={etaSec ? `Typical ${run.kind} run here: ${fmtSec(etaSec)}` : ''}>
      {fmtSec(elapsed)} elapsed{remaining != null ? (over ? ' · taking longer than usual' : remaining < 5 ? ' · wrapping up' : ` · ~${fmtSec(remaining)} left`) : ''}
    </span>
  );
}

export function ActiveBar({ org, run, canEdit, busy, act, etaSec = null }) {
  if (!ACTIVE.has(run.status)) return null;
  return (
    <div className="lab-run-actions lab-activebar">
      <span className="lab-pulse" aria-hidden="true" />
      <span className="mf-muted">{run.stage || 'working…'}</span>
      <RunClock run={run} etaSec={etaSec} />
      <span style={{ flex: 1 }} />
      {canEdit && <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" disabled={busy === `cancel-${run.id}`} onClick={() => act(`cancel-${run.id}`, () => apiPost(`/lab/${org.id}/runs/${run.id}/cancel`, {}))}>Cancel</button>}
    </div>
  );
}

export function UsageLine({ run }) {
  const u = run.usage;
  if (!u) return null;
  return (
    <p className="mf-muted" style={{ margin: 0, fontSize: 12 }}>
      {u.searchCalls ? `${u.searchCalls} web searches · ` : ''}{u.browserSec ? `${Math.ceil(u.browserSec / 60)} browser min · ` : ''}{u.tokens ? `${u.tokens.toLocaleString()} tokens` : ''}
      {run.approval ? ` · approved by ${run.approval.email || run.approval.uid} ${when(run.approval.at)}` : ''}
    </p>
  );
}
